import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, readoutStyle, titleStyle } from '../theme';
import { createButton } from '../ui/button';
import { createSettingsButton } from '../ui/settingsPanel';
import { maybeShowTutorial, createAdviserButton, showAdviser } from '../ui/adviserPanel';
import { isComplete, hintTextKey } from '@core/tutorial/tutorial';
import { layoutOf, makeTappable, watchResize, MIN_HIT } from '../ui/layout';
import { onTap } from '../ui/tap';
import type { Campaign } from '@core/campaign/campaign';
import {
  RESEARCH_SHIP,
  SUBMARINE,
  SPEED_SETTINGS,
  type SpeedSetting,
  type VesselId,
} from '@core/voyage/profiles';
import { saveCampaign } from '../saveGame';
import { audio } from '../audio/engine';
import { TIME_SCALES, type WorldMapScene } from './WorldMapScene';
import { ACTION_BAR_HEIGHT_KEY, HEADER_HEIGHT_KEY } from './HudScene';

/**
 * Kopfzeile und Bedienleiste ueber dem Lagebild.
 *
 * Als eigene Szene aus demselben Grund wie `HudScene`: Objekte der Kartenszene
 * skalieren zwangslaeufig mit der Kamera, und die faehrt zwischen einfacher und
 * hundertfacher Vergroesserung. Text waere dort entweder unlesbar klein oder
 * bildschirmfuellend.
 *
 * Hier steht alles, was frueher die Stationsszene war — nur eben ueber der
 * Karte statt an ihrer Stelle.
 *
 * Die Bedienleiste bricht um, wenn sie nicht in eine Zeile passt. Frueher
 * standen alle Schaltflaechen stur nebeneinander und liefen auf einem Telefon
 * links und rechts aus dem Bild.
 */

type Panel = 'main' | 'wait';

interface Action {
  readonly label: string;
  readonly onClick: () => void;
  /** Hervorgehoben — etwa die gerade gewaehlte Fahrtstufe. */
  readonly active?: boolean;
}

/** Wartezeiten zur Auswahl. Der alte feste Schritt von 12 h bleibt dabei. */
const WAIT_STEPS = [3, 6, 12] as const;

export class MapHudScene extends Phaser.Scene {
  private campaign!: Campaign;
  /** Welche Leiste unten steht — die Hauptleiste oder die Wartezeiten. */
  private panel: Panel = 'main';

  constructor() {
    super('MapHud');
  }

  /**
   * Das Fahrzeug, aus dessen Sicht die Lage gerechnet wird.
   *
   * Bewusst kein eigenes Feld, sondern ein Blick auf die Kartenszene. Vorher
   * hielt diese Leiste eine eigene Kopie, die nur beim Erzeugen einmal
   * abgeschrieben wurde — und lief damit auseinander, sobald die Karte den
   * Fokus von sich aus wechselte: beim Losschicken, bei einem Ereignis, bei
   * der Ankunft. Der Fokus gehoert der Karte; hier wird er nur gelesen.
   */
  private get viewpoint(): VesselId {
    return this.worldMap.focused;
  }

  private get worldMap(): WorldMapScene {
    return this.scene.get('WorldMap') as WorldMapScene;
  }

  init(): void {
    this.panel = 'main';
  }

  create(): void {
    this.campaign = this.registry.get('campaign') as Campaign;
    this.redraw();
    watchResize(this, () => this.redraw());
    this.bindKeys();
  }

  /** Die Station ist der einzige sichere Punkt — nur dort wird gespeichert. */
  private get atBase(): boolean {
    return this.campaign.isAtBase(this.viewpoint);
  }

  /**
   * Zeichnet die Leiste neu.
   *
   * Oeffentlich, weil die Kartenszene sie anstoesst: Wer ein Fahrzeug
   * losschickt oder heimkommen laesst, aendert damit auch, was hier an
   * Schaltflaechen stehen darf.
   */
  refresh(): void {
    this.panel = 'main';
    this.redraw();
  }

  private redraw(): void {
    // Offene Dialoge (Einsatzplanung, Einstellungen) liegen in dieser Szene;
    // sie leben ueber `depth >= 1500` und bleiben vom Neuzeichnen verschont.
    for (const child of [...this.children.list]) {
      if ((child as unknown as { depth: number }).depth < 1500) child.destroy();
    }
    if (this.atBase) saveCampaign(this.campaign);

    const layout = layoutOf(this);
    const headerBottom = this.renderHeader(layout.centreX, layout.width);
    this.registry.set(HEADER_HEIGHT_KEY, headerBottom);

    const barHeight = this.renderActions(layout.width, layout.height);
    this.registry.set(ACTION_BAR_HEIGHT_KEY, barHeight);

    this.renderZoom(layout.width, layout.height - barHeight, headerBottom);

    maybeShowTutorial(this, this.campaign, 'calls');
  }

  // ------------------------------------------------------------------ Kopf

  /** Liefert die Unterkante der Kopfleiste. */
  private renderHeader(centreX: number, width: number): number {
    // Rechts oben sitzen Einstellungen und Roskopf; die Zeilen duerfen nicht
    // darunter laufen — auf einem Telefon ist das der halbe Bildschirm.
    const sideReserve = width < 700 ? 0 : 150;
    const wrapWidth = width - 24 - sideReserve * 2;

    // Schmal: Titel links, Einstellungen rechts in derselben Zeile — mittig
    // gesetzt lagen beide uebereinander. Roskopf wandert dann in die Leiste.
    const narrow = sideReserve === 0;
    const title = this.add
      .text(narrow ? 12 : centreX, 12, t(this.atBase ? 'base.title' : 'base.title.atsea'), titleStyle(15))
      .setOrigin(narrow ? 0 : 0.5, 0);

    let y = title.y + title.height + 6;
    if (narrow) {
      createSettingsButton(this, width - 12, title.y + title.height / 2, () => this.refreshAll());
    }

    const clock = this.add
      .text(centreX, y, this.clockLine(), {
        ...readoutStyle(11, true),
        align: 'center',
        wordWrap: { width: wrapWidth },
      })
      .setOrigin(0.5, 0);
    y = clock.y + clock.height + 4;

    const tally = this.add
      .text(centreX, y, this.tallyLine(), {
        ...readoutStyle(11, true),
        align: 'center',
        color: PALETTE.inkFaint,
        wordWrap: { width: wrapWidth },
      })
      .setOrigin(0.5, 0);
    y = tally.y + tally.height + 4;

    const vessel = this.renderVesselLine(centreX, y, wrapWidth);
    const barHeight = vessel.y + vessel.height + 10;

    const bar = this.add.rectangle(0, 0, width, barHeight, PALETTE.abyss, 0.72).setOrigin(0, 0);
    bar.setStrokeStyle(0);
    // Der Balken entsteht zuletzt und muss deshalb ausdruecklich nach hinten.
    bar.setDepth(-1);

    if (sideReserve > 0) {
      createSettingsButton(this, width - 16, title.y + title.height / 2, () => this.refreshAll());
      createAdviserButton(this, width - 16, clock.y + clock.height / 2, this.campaign, 'calls');
    }

    return barHeight;
  }

  private clockLine(): string {
    const clock = this.campaign.clockHours;
    const day = Math.floor(clock / 24) + 1;
    const hour = Math.floor(clock % 24);
    // Das Ansehen steht nicht mehr allein da, sondern mit seiner Folge: wie
    // spaet ein Notruf hier ankommt. Eine Zahl ohne Wirkung ist Zierat.
    const delay = Math.round(this.campaign.reportDelay);
    const standing =
      delay <= 0
        ? t('base.reputation.instant', { value: this.campaign.reputation })
        : t('base.reputation.delayed', { value: this.campaign.reputation, hours: delay });

    // Die Frist steht gleich neben der Uhr — sie ist die zweite Uhr des Spiels.
    const deadline = this.campaign.deadlineHours;
    const hearing =
      deadline === null
        ? null
        : t('base.deadline', { days: Math.max(0, Math.ceil((deadline - clock) / 24)) });

    return [
      t('base.clock', { day, hour: String(hour).padStart(2, '0') }),
      ...(hearing === null ? [] : [hearing]),
      standing,
      t('base.act', { act: this.campaign.currentAct }),
      t('base.progress', {
        collected: this.campaign.cluesCollected,
        total: this.campaign.cluesPossible,
      }),
    ].join('  ·  ');
  }

  /**
   * Die Bilanz — dauerhaft sichtbar.
   *
   * Bisher stand sie erst im Finale. Aber das ganze Spiel dreht sich darum,
   * was die Wahrheit kostet: Harte Beweise liegen nur dort, wo niemand mehr
   * zu retten war. Diese Rechnung soll man waehrend des Spiels vor Augen haben,
   * nicht erst am Ende vorgelegt bekommen.
   */
  private tallyLine(): string {
    const toll = this.campaign.toll;
    return [
      t('toll.rescued', { count: toll.rescued }),
      t('toll.lost', { count: toll.lost }),
      t('toll.morale', { value: Math.round(toll.morale), mood: t(toll.moodKey) }),
    ].join('  ·  ');
  }

  /**
   * Wo das Fahrzeug steht und wie es dasteht — antippbar, um zwischen Schiff
   * und U-Boot zu wechseln. Auf See ist der Treibstoff die eigentliche
   * Nachricht: Es gibt keinen Nachschub, und die Strecke nach Hause muss
   * uebrig bleiben.
   */
  private renderVesselLine(centreX: number, y: number, wrapWidth: number): Phaser.GameObjects.Text {
    const condition = this.campaign.conditionOf(this.viewpoint);
    const profile = this.viewpoint === 'ship' ? RESEARCH_SHIP : SUBMARINE;

    /*
     * Waehrend einer Fahrt nennt diese Zeile keine Verbrauchswerte.
     *
     * Die Leiste zeichnet sich nur bei Ereignissen neu, die Fahrtanzeige unten
     * dagegen in jedem Bild. Standen die Zahlen an beiden Stellen, meldete die
     * Kopfzeile den Stand beim Ablegen, waehrend daneben der wirkliche stand.
     */
    const underway = this.campaign.isUnderway(this.viewpoint);
    const disabled = this.campaign.needsAssistance(this.viewpoint);

    const text = (
      underway
        ? [t(profile.nameKey), t('base.underway')]
        : [
            t(profile.nameKey),
            t('base.fuel', {
              used: Math.round(condition.fuel),
              available: Math.round(profile.fuelCapacity),
            }),
            t('base.hull', { value: Math.round(condition.hull) }),
            disabled
              ? t('base.disabled')
              : this.atBase
                ? t('base.at_station')
                : t('base.at_sea', {
                    miles: Math.round(this.campaign.distanceHome(this.viewpoint)),
                  }),
          ]
    ).join('  ·  ');

    const colour = disabled ? TEXT_COLOUR.danger : PALETTE.inkDim;
    const label = this.add
      .text(centreX, y, `${text}  ⇄`, {
        ...readoutStyle(11, true),
        color: colour,
        align: 'center',
        wordWrap: { width: wrapWidth },
      })
      .setOrigin(0.5, 0);

    makeTappable(label);
    onTap(label, {
      onTap: () => {
        audio.cue('select');
        this.switchVessel();
      },
      onHighlight: (on) => label.setColor(on ? PALETTE.ink : colour),
    });

    return label;
  }

  private switchVessel(target?: VesselId): void {
    // Karte und Leiste meinen dasselbe Fahrzeug, weil es nur eines gibt:
    // Umschalten heisst, der Karte einen neuen Fokus zu geben. `setFocus`
    // zeichnet diese Leiste selbst neu.
    const next: VesselId = target ?? (this.viewpoint === 'ship' ? 'submarine' : 'ship');
    this.worldMap.setFocus(next);
  }

  // --------------------------------------------------------------- Bedienung

  /** Was unten stehen darf — je nach Lage des betrachteten Fahrzeugs. */
  private actions(): Action[] {
    const actions: Action[] = [];
    const vesselId = this.viewpoint;

    if (this.panel === 'wait') {
      for (const hours of WAIT_STEPS) {
        actions.push({
          label: t('base.wait.hours', { hours }),
          onClick: () => this.doWait(() => this.campaign.wait(hours)),
        });
      }
      actions.push({
        label: t('base.wait.call'),
        onClick: () => this.doWait(() => this.campaign.waitForNextCall()),
      });
      actions.push({
        label: t('base.wait.back'),
        onClick: () => {
          this.panel = 'main';
          this.redraw();
        },
      });
      return actions;
    }

    /*
     * Wer faehrt, wartet nicht.
     *
     * Warten, Ankern, Tanken und Heimfahren setzen alle voraus, dass das
     * Fahrzeug steht. Waeren sie waehrend einer Fahrt antippbar, koennte man
     * die Uhr zweimal gleichzeitig vorstellen.
     *
     * Was man unterwegs aber tun kann, ist die Fahrtstufe wechseln. Bisher
     * ging das nur beim Auslaufen — eine Prognose, die unterwegs kippt, liess
     * sich nicht mehr beantworten.
     */
    const mission = this.campaign.missionOf(vesselId);
    if (mission !== null) {
      const current = mission.voyage.state.speed;
      for (const setting of SPEED_SETTINGS) {
        actions.push({
          label: t(`briefing.speed.${setting}`),
          active: setting === current,
          onClick: () => this.setSpeed(setting),
        });
      }
      // Zeitraffer: schaltet reihum durch 1×, 2×, 4×.
      const scale = this.worldMap.timeScale;
      actions.push({
        label: t('base.timescale', { scale }),
        active: scale > 1,
        onClick: () => {
          const index = TIME_SCALES.indexOf(scale);
          this.worldMap.timeScale = TIME_SCALES[(index + 1) % TIME_SCALES.length] ?? 1;
          audio.cue('select');
          this.redraw();
        },
      });
    } else if (this.campaign.needsAssistance(vesselId)) {
      /*
       * Liegengeblieben.
       *
       * Ohne Treibstoff oder mit zerschlagenem Rumpf bleibt nur der
       * Schlepper. Er kostet Zeit — waehrenddessen laufen alle Notrufe weiter
       * ab — und Ansehen.
       */
      const cost = this.campaign.assistanceCost(vesselId);
      actions.push({
        label: t('base.tow', {
          hours: Math.round(cost.hours),
          reputation: cost.reputation,
        }),
        onClick: () => {
          this.campaign.requestAssistance(vesselId);
          audio.cue('creak');
          this.refreshAll();
        },
      });
    } else if (this.atBase) {
      actions.push({
        label: t('base.wait.menu'),
        onClick: () => {
          this.panel = 'wait';
          this.redraw();
        },
      });

      const condition = this.campaign.vessels[vesselId];
      const profile = vesselId === 'ship' ? RESEARCH_SHIP : SUBMARINE;
      if (condition.fuel < profile.fuelCapacity - 0.5 || condition.hull < 99.5) {
        actions.push({
          label: t('base.resupply'),
          onClick: () => {
            this.campaign.resupply(vesselId);
            audio.cue('select');
            this.refreshAll();
          },
        });
      }
    } else {
      /*
       * Auf See vor Anker gehen.
       *
       * Liegenbleiben ist die naheliegendste Entscheidung von allen: Man steht
       * dort, wo etwas passiert ist, spart die halbe Ueberfahrt zum naechsten
       * Ruf — und verbraucht dabei keinen Treibstoff, hat aber auch keine
       * Werft in Reichweite.
       */
      actions.push({
        label: t('base.anchor'),
        onClick: () => {
          this.campaign.wait(12);
          audio.cue('select');
          this.refreshAll();
        },
      });

      // Auf See gibt es nur einen Weg zurueck, und der kostet dieselbe Zeit
      // wie ein Einsatz.
      actions.push({
        label: t('base.go_home'),
        onClick: () => {
          this.campaign.dispatch({
            vesselId,
            incident: null,
            speed: 'cruise',
            homebound: true,
          });
          audio.cue('select');
          this.refreshAll();
        },
      });
    }

    actions.push({
      label: t('base.evidence', { count: this.campaign.ledger.size }),
      onClick: () => this.openEvidence(),
    });

    if (this.scale.width < 700 && isComplete(this.campaign.tutorial)) {
      actions.push({
        label: t('adviser.ask'),
        onClick: () => showAdviser(this, { text: t(hintTextKey('calls')) }),
      });
    }

    /*
     * Der Bericht kann ab Akt II vorgelegt werden, nicht erst mit voller Akte.
     *
     * Vorher war das Finale erst bei 14 von 14 Hinweisen offen — und dann
     * sprach alles fuer die Wahrheit. Jetzt ist der Zeitpunkt selbst die
     * Entscheidung: frueher urteilen heisst weniger Tote, aber mehr Risiko.
     */
    if (this.campaign.canFileReport) {
      actions.push({
        label: t(this.campaign.isReadyForFinale ? 'base.finale' : 'base.finale.early'),
        active: this.campaign.isReadyForFinale,
        onClick: () => {
          this.scene.stop();
          this.scene.stop('WorldMap');
          this.scene.start('Finale');
        },
      });
    }

    return actions;
  }

  /**
   * Setzt die Schaltflaechen in Zeilen, die in die Breite passen, und liefert
   * die Hoehe der ganzen Leiste.
   *
   * Zweimal bauen: einmal ausserhalb des Bildes, um die tatsaechlichen Breiten
   * zu erfahren, dann endgueltig an der richtigen Stelle. Die Schaltflaechen
   * wachsen mit ihrer Beschriftung.
   */
  private renderActions(width: number, height: number): number {
    const actions = this.actions();
    const gap = 8;
    const available = width - 16;
    const style = { width: 96, height: MIN_HIT, fontSize: 11 };

    const widths = actions.map((action) => {
      const probe = createButton(this, -9999, -9999, action.label, () => {}, style);
      const measured = probe.width;
      probe.destroy();
      return Math.min(measured, available);
    });

    // Zeilen fuellen, solange es passt.
    const rows: number[][] = [[]];
    let rowWidth = 0;
    widths.forEach((w, index) => {
      const row = rows[rows.length - 1] as number[];
      const needed = row.length === 0 ? w : rowWidth + gap + w;
      if (needed > available && row.length > 0) {
        rows.push([index]);
        rowWidth = w;
      } else {
        row.push(index);
        rowWidth = needed;
      }
    });

    const rowHeight = MIN_HIT + gap;
    const barHeight = rows.length * rowHeight + 12;
    const top = height - barHeight + 6;

    rows.forEach((row, rowIndex) => {
      const total = row.reduce((sum, i) => sum + (widths[i] as number), 0) + (row.length - 1) * gap;
      let x = width / 2 - total / 2;
      const y = top + rowIndex * rowHeight + MIN_HIT / 2;
      for (const index of row) {
        const action = actions[index] as Action;
        const w = widths[index] as number;
        const button = createButton(this, x + w / 2, y, action.label, action.onClick, {
          ...style,
          width: w,
        });
        if (action.active === true) {
          (button.list[0] as Phaser.GameObjects.Rectangle).setStrokeStyle(1.5, PALETTE.coast);
          (button.list[1] as Phaser.GameObjects.Text).setColor(TEXT_COLOUR.coast);
        }
        x += w + gap;
      }
    });

    // Ein ruhiger Grund hinter der Leiste, damit Karte und Knoepfe sich nicht mischen.
    this.add
      .rectangle(0, height - barHeight, width, barHeight, PALETTE.abyss, 0.6)
      .setOrigin(0, 0)
      .setDepth(-1);

    return barHeight;
  }

  /**
   * Zoom zum Antippen, rechts ueber der Leiste.
   *
   * Auf einem Telefon gibt es kein Mausrad; Zusammenziehen mit zwei Fingern
   * geht zwar auch, aber findet man nur, wenn man es erwartet.
   */
  private renderZoom(width: number, bottom: number, headerBottom: number): void {
    const size = MIN_HIT;
    const x = width - 12 - size / 2;
    const plusY = bottom - 12 - size * 1.5 - 6;
    if (plusY - size / 2 < headerBottom + 8) return;

    createButton(this, x, plusY, '+', () => this.worldMap.zoomStep(1), {
      width: size,
      height: size,
      fontSize: 16,
    });
    createButton(this, x, plusY + size + 6, '−', () => this.worldMap.zoomStep(-1), {
      width: size,
      height: size,
      fontSize: 16,
    });
  }

  // ---------------------------------------------------------------- Handlungen

  private setSpeed(setting: SpeedSetting): void {
    const mission = this.campaign.missionOf(this.viewpoint);
    if (mission === null) return;
    mission.voyage.setSpeed(setting);
    audio.cue('select');
    this.redraw();
  }

  private doWait(wait: () => void): void {
    wait();
    audio.cue('select');
    this.panel = 'main';
    this.refreshAll();
  }

  private openEvidence(): void {
    this.scene.stop();
    this.scene.stop('WorldMap');
    this.scene.start('Evidence');
  }

  /**
   * Tastenkuerzel fuer den Schreibtisch.
   *
   * Nur, wenn gerade kein Dialog offen ist — sonst liefe mit „W“ die Uhr
   * weiter, waehrend man in der Einsatzplanung liest.
   */
  private bindKeys(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.dialogOpen()) return;
      const key = event.key.toLowerCase();
      const vesselId = this.viewpoint;
      const idle = !this.campaign.isUnderway(vesselId) && !this.campaign.needsAssistance(vesselId);

      switch (key) {
        case 'e':
        case 'b':
          this.openEvidence();
          break;
        case 'w':
          if (idle) this.doWait(() => this.campaign.wait(12));
          break;
        case '1':
          this.switchVessel('ship');
          break;
        case '2':
          this.switchVessel('submarine');
          break;
        case '+':
        case '=':
          this.worldMap.zoomStep(1);
          break;
        case '-':
          this.worldMap.zoomStep(-1);
          break;
        case 'escape':
          if (this.panel !== 'main') {
            this.panel = 'main';
            this.redraw();
          }
          break;
      }
    });
  }

  /** Liegt gerade ein Dialog ueber der Karte? */
  private dialogOpen(): boolean {
    return this.children.list.some(
      (child) => (child as unknown as { depth: number }).depth >= 1500,
    );
  }

  /**
   * Zeichnet Leiste und Karte neu.
   *
   * Nach jedem Schritt, der Zeit kostet: Notrufe koennen abgelaufen sein, neue
   * dazugekommen, und die Marker haengen an beidem.
   */
  private refreshAll(): void {
    this.worldMap.refreshIdle();
    this.redraw();
  }
}
